<script lang="ts" setup>
import 'vidstack/player/styles/base.css';
import 'vidstack/player';
import 'vidstack/player/ui';
import 'vidstack/icons';

import { isHLSProvider, type MediaCanPlayEvent, type MediaProviderChangeEvent } from 'vidstack';
import type { MediaPlayerElement } from 'vidstack/elements';
import { computed, onMounted, ref } from 'vue';

import { PlayerStore } from '@/front/stores/player_store.js';
import { VideoStore } from '@/front/stores/video_store.js';
import { UploadStore } from '@/front/stores/upload_store.js';

const playerStore = PlayerStore();
const videoStore = VideoStore();
const uploadStore = UploadStore();

const $loopPlayer = ref<MediaPlayerElement>();
const $src = ref('');
const domVideo = ref<HTMLVideoElement | null>(null);

const emit = defineEmits(['ended']);

const isIOSWebView = computed(() => {
  const userAgent = navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/.test(userAgent);
  return isIOS && playerStore.isWebView;
});

const useNativeLoopVideo = computed(() => isIOSWebView.value);

function getVideoSource(video) {
  return uploadStore.uploadFile && uploadStore.uploadSource
    ? uploadStore.uploadSource
    : video.source?.hls || video.source?.mp4 || video.source?.s3 || '';
}

onMounted(() => {
  $src.value = getVideoSource(videoStore.video);
  log.info(3, '🌜 LoopingPlayer $src', $src.value);
});

function onProviderChange(event: MediaProviderChangeEvent) {
  const provider = event.detail;
  if (isHLSProvider(provider)) {
    provider.config = {};
  }
}

function onEnded() {
  emit('ended');
}

function onCanPlay(event: MediaCanPlayEvent) {
  setTimeout(() => {
    domVideo.value = $loopPlayer.value?.querySelector('video') ?? null;
    if (!domVideo.value) {
      return;
    }
    domVideo.value.muted = true;
    domVideo.value.volume = 0;
    domVideo.value.play();
  }, 10);
}

function onTimeUpdate(event: MediaTimeUpdateEvent) {
  const t = Math.floor(event.detail.currentTime);
  if (t > 5 && domVideo.value) {
    domVideo.value.currentTime = 0;
  }
}

const getCrossOrigin = computed(() => {
  if ($src.value.includes('cloudfront')) {
    return false;
  } else if ($src.value.includes('panopto')) {
    return 'anonymous';
  } else {
    return 'anonymous';
  }
});

const nativeCrossOrigin = computed(() => {
  return getCrossOrigin.value === false ? null : getCrossOrigin.value;
});

const onError = (event) => {
  log.info(5, '🌜 LoopingPlayer onError', event);
  if (videoStore.video.source.backup_hls && $loopPlayer.value?.src != videoStore.video.source.backup_hls) {
    setTimeout(() => {
      log.info(5, '🌜 LoopingPlayer onError, switching to backup HLS', videoStore.video.source.backup_hls);
      if ($loopPlayer.value) {
        $loopPlayer.value.src = videoStore.video.source.backup_hls;
      }
    }, 50);
  } else {
    playerStore.error = event?.detail?.message || 'Preview failed to load';
  }
};

const onNativeCanPlay = () => {
  if (!domVideo.value) {
    return;
  }
  domVideo.value.muted = true;
  domVideo.value.volume = 0;
  domVideo.value.play();
};

const onNativeTimeUpdate = () => {
  if (!domVideo.value) {
    return;
  }
  const t = Math.floor(domVideo.value.currentTime);
  if (t > 5) {
    domVideo.value.currentTime = 0;
  }
};

const onNativeError = () => {
  if (videoStore.video.source.backup_hls && domVideo.value && domVideo.value.src != videoStore.video.source.backup_hls) {
    domVideo.value.src = videoStore.video.source.backup_hls;
    domVideo.value.load();
    domVideo.value.play();
  } else {
    playerStore.error = 'Preview failed to load';
  }
};

const loadStrategy = computed(() => {
  if (playerStore.isMobile && !playerStore.isWebView) {
    return 'idle';
  }
  return 'eager';
});
</script>

<template>
  <div class="loop-player-container">
    <video
      v-if="useNativeLoopVideo"
      ref="domVideo"
      class="loop-player-video"
      :class="{ 'cover-mode': videoStore.video?.design?.cover_mode }"
      :src="$src"
      :crossorigin="nativeCrossOrigin"
      muted
      playsinline
      autoplay
      loop
      preload="auto"
      @ended="onEnded"
      @canplay="onNativeCanPlay"
      @timeupdate="onNativeTimeUpdate"
      @error="onNativeError"
    />

    <media-player
      v-else
      :load="loadStrategy"
      class="loop-player w-full h-full bg-black text-white font-sans overflow-hidden ring-media-focus data-[focus]:ring-4"
      :class="{ 'cover-mode': videoStore.video?.design?.cover_mode }"
      :src="$src"
      :crossOrigin="getCrossOrigin"
      playsInline
      loop
      :muted="true"
      @ended="onEnded"
      @provider-change="onProviderChange"
      @time-update="onTimeUpdate"
      @error="onError"
      @can-play="onCanPlay"
      ref="$loopPlayer"
    >
      <media-provider class="loop-media-provider">
      </media-provider>
    </media-player>
  </div>
</template>

<style>
.loop-player-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.loop-player,
.loop-player .loop-media-provider,
.loop-player-video {
  width: 100%;
  height: 100%;
}

.loop-player video,
.loop-player-video {
  object-fit: contain;
  object-position: center center;
  background: black;
}

.loop-player.cover-mode video,
.loop-player-video.cover-mode {
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
}
</style>
